import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
// import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';

import ChatBubble, { ChatBubbleSmall } from './ChatBubble';

import IdentityAvatar from './IdentityAvatar';

import { observer } from 'mobx-react-lite';

import { MessageProps } from '../stores/messages_store';

import { CollabProps } from '../stores/collab_store';
import { Clock } from '../stores/clock_store';
import useClock  from '../hooks/useClock';
import useMessages  from '../hooks/useMessages';
import useUserIdentity  from '../hooks/useUserIdentity';
import useWorkItem  from 'hooks/useWorkItem';
import useCollaborators  from 'hooks/useCollaborators';
import useRating  from 'hooks/useRating';
import useFileAssociation from 'hooks/useFileAssociation';
import { getIcon } from 'utils/misc';
import { RootStoreContext } from '../stores/root_store';
import { UserIdentityContext } from '../contexts/user_identity_context';
import { Success } from 'components/FancyToast';
import { deleteCachedComment, CommentSubmitProps } from './CommentInput';
import CombinedInput from 'components/CombinedInput';
import { Checkbox } from '@mui/joy';
import RatingCardThumbs from './RatingCardThumbs';
import { CollaboratorsProps } from 'stores/collaborators_store';
import FancyIcon from './FancyIcon';


type MessagesPaneProps = {
	// currentRequestKey: string;
	// request: Request | null;
    associated_type: string;
    associated_uuid: string;
    bubbleSize?: string | undefined;
    collab?: CollabProps;
    onMessageSubmit?: (comment: CommentSubmitProps) => boolean;
    onHeightChange?: (height: number) => void;
    onHide?: (hidden: boolean) => void;
};

type MessagesListProps = {
	collab?: CollabProps;
    bubbleSize?: string | undefined;
    associated_type: string;
    associated_uuid: string;
};


function ClockInOutDivider({ comment, previousMessage }: { comment: Clock & { out: boolean }, previousMessage?: Clock|undefined }) {
    const in_or_out = (comment?.out ? 'Out' : 'In');

    const omitIdentity = previousMessage && (previousMessage?.identity == comment.identity &&
        dayjs(comment.date_created).diff(dayjs(previousMessage.date_created), 'minute') < 5);

    return (
        <Stack sx={{
            opacity: 0.8,
        }}>
            <ChatBubbleSmall
                omitIdentity={omitIdentity}
                content={
                    <Typography
                        level="body-xs"
                        startDecorator={<FancyIcon  name={`DayOnesClock${in_or_out}`} />}
                    >
                        {`Clocked ${in_or_out}`}
                    </Typography>
                }
                identityKey={comment.identity}
                content_type="react"
                date_created={comment.date_created} />
        </Stack>
    );
}


function MessageItem({ message, bubbleSize, previousMessage }: { message: MessageProps, bubbleSize?: string, previousMessage?: MessageProps|undefined }) {
    const { fetchListByAssociation, getListByAssociation, items } = useFileAssociation();
    const [fileAssociations, setFileAssociations] =
        useState(getListByAssociation('message', message.uuid));
    const { currentIdentity } = useUserIdentity();

    useEffect(() => {
        fetchListByAssociation(currentIdentity?.uuid, "message", message.uuid)
            .then((fetchedItems) => {
                // console.log("Fetched items", fetchedItems);
                setFileAssociations(fetchedItems);
            });
    }, [message.uuid, currentIdentity?.uuid]);

    useEffect(() => {
        setFileAssociations(getListByAssociation('message', message.uuid));
        // console.log("File association items", items);
    }, [items.length]);


	// function get_content(message : MessageProps) {
	// 	if(message.content_plain) {
	// 		return { content: message.content_plain, content_type: 'text/plain' };
	// 	}
	// 	return { content: message.content, content_type: message.content_type };
	// }

    // const content = get_content(message);


    // if it's the same identity and less than 5 minutes since the last message, omit the identity
    const omitIdentity = (previousMessage?.identity == message.identity &&
        dayjs(message.date_created).diff(dayjs(previousMessage.date_created), 'minute') < 5);

    console.log(message);
    if (bubbleSize == 'small') {
        // console.log("previous", previousMessage, message.identity, omitIdentity);
        return (
            <ChatBubbleSmall
                uuid={message.uuid}
                file_associations={fileAssociations}
                identityKey={message.identity}
                omitIdentity={omitIdentity}
                content={message.content}
                original_content={message.original_content}
                content_type={message.content_type}
                context_type={message.context_type}
                context_uuid={message.context_uuid}
                date_created={message.date_created}
            />
        );
    }
    else {
        return (
            <ChatBubble
                uuid={message.uuid}
                file_associations={fileAssociations}
                identityKey={message.identity}
                omitIdentity={omitIdentity}
                content={message.content}
                content_type={message.content_type}
                original_content={message.original_content}
                context_type={message.context_type}
                context_uuid={message.context_uuid}
                date_created={message.date_created}
            />
        );
    }
}


const MessagesList = observer(({ collab, associated_type, associated_uuid, bubbleSize } : MessagesListProps) => {
	const rootStore = React.useContext(RootStoreContext);
    const { fetchListByAssociation, getListByAssociation } = useMessages();
    const { getClocksForCollab } = useClock();
    const clockStarts = getClocksForCollab ?  getClocksForCollab(collab?.key || '') : [];
    const belowTheMessages = useRef<HTMLDivElement>(null);
    const { getMessagesForCollab } = useMessages();
    const { currentIdentity } = useUserIdentity();
    //    const [firstLoad, setFirstLoad] = useState(true);

    const messages = (collab?.key ?
        getMessagesForCollab(collab?.key || '') :
        getListByAssociation(associated_type, associated_uuid)
    );

    const clockEnds = clockStarts.slice().filter((i) => i.time_end != null)
        .map((i) => {
            return {...i, out: true, date_created: i.time_end }
        });


    const clocksAndMessages = [...messages, ...clockStarts, ...clockEnds].sort((a, b) => {
 			return a.date_created > b.date_created ? 1 : -1;
 		});


	useEffect(() => {
		if(collab?.key && currentIdentity.uuid) {
			rootStore.messagesStore.fetchMessages(collab.key, currentIdentity.uuid);
		}
        if(currentIdentity.uuid) {
            console.log("Fetching messages for", associated_type, associated_uuid);
            fetchListByAssociation(currentIdentity.uuid, associated_type, associated_uuid);
        }

	}, [rootStore.messagesStore, collab?.key, currentIdentity.uuid]);


    useEffect(() => {
        if (messages.length) {
            //            if (!firstLoad) {
            belowTheMessages.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
            // }
            // setFirstLoad(false);
        }
    }, [messages.length]);


    const CheckIcon = getIcon('Check');

	return (

            <Stack
                direction="column"
                spacing={2}

                // divider={ <Divider /> }
                sx={{
                    overflowY: 'auto'
                }}

            >
				{clocksAndMessages.map((comment, index: number) => {
                    if((comment as Clock)?.time_start || (comment as Clock)?.time_end) {
                        return <ClockInOutDivider key={index}
                                   previousMessage={(clocksAndMessages[index - 1] as Clock)}
                                   comment={comment as Clock & {out: boolean}} />;
                    }
                    else {
                        return <MessageItem
                                   previousMessage={(clocksAndMessages[index - 1] as MessageProps)}
                                   bubbleSize={bubbleSize}
                                   key={index}
                                   message={comment as MessageProps} />;
                    }
				})}


                { collab && collab.variant != 'support'  &&
                    <CompleteUI collab={collab} />
                }
                <Box

                    sx={{
                        p: 8,
                        display: { xs: 'none', sm: 'block' },
                    }}
                    ref={belowTheMessages}>
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}>
                        <Typography
                            startDecorator={<CheckIcon />}
                            level="body-xs">
                        That's it!
                    </Typography>
                    </Stack>
                </Box>

            </Stack>


	);
});

const CollabRating = observer(({ collab, currentIdentityUuid, cb }: { collab: CollabProps, currentIdentityUuid: string, cb: CollaboratorsProps }) => {
    const { rateItem } = useRating();
    const [score, setScore] = useState(0);
    const [hidden, setHidden] = useState(false);

    if (hidden) return null;
    return (

        <RatingCardThumbs
            label={<Typography level="title-sm">
                Rate this collaboration with
                <IdentityAvatar
                    size="chip"
                    interactive={false}
                    identityKey={cb.identity} />
            </Typography>}
            setScore={(s) => {
                setScore(s);
                rateItem(collab.uuid, s, currentIdentityUuid, cb.identity).
                    then(() => {
                        Success("Saved rating, thanks for the feedback!");
                        setHidden(true);
                    });
            }}
            score={score}
        />

    );
});

const CompleteUI = observer(({ collab } : { collab: CollabProps }) => {
    const { currentIdentity } = useUserIdentity();
    const { complete } = useWorkItem();
    const { getCollaborators, fetchListForCollab } = useCollaborators();
    const [changed, setChanged] = useState(false);
    const [completed, setCompleted] = useState(collab.completed);
    const [loaded, setLoaded] = useState(false);

    function onCheck(
        item: CollabProps,
        checked: boolean
    ) {
        console.log('onCheck', item, checked);
        setChanged(true);
            setCompleted(checked);
        complete(currentIdentity.uuid, collab.uuid, checked).then(() => {
            Success("Collaboration marked as " + (checked ? "complete" : "incomplete"));
        });
    }

    // All collaborators except yourself.
    const cbs = getCollaborators(collab.uuid).filter((c) => c.identity != currentIdentity?.uuid);
    useEffect(() => {
        if(!currentIdentity.uuid) return;
        if(loaded) return;
        setLoaded(true); // close enough
        fetchListForCollab(currentIdentity.uuid, collab.uuid);
    }, [currentIdentity.uuid, collab.uuid]);

    if (currentIdentity.uuid != collab.identity) return null;

    return (
        <Stack spacing={2} pt={3}>


        <FormControl>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                    onChange={(e) => onCheck(collab, e.target.checked)}
                    checked={completed}
                    label="Completed"
                    size="lg" />
            </Stack>

            <FormHelperText>
                { !completed &&
                <Typography level='body-xs'>
                    Mark this as completed and rate your experience.
                </Typography>
                }
                { completed &&
                <Typography level='body-xs'>
                  This is marked as completed.
                </Typography>
                }

            </FormHelperText>

            { changed && completed &&
                <Stack mt={1}>
                    { cbs.map((cb) =>
                       <CollabRating
                            key={cb.uuid}
                            collab={collab}
                            currentIdentityUuid={currentIdentity.uuid}
                            cb={cb} />
                    )
                    }
                </Stack>
            }


        </FormControl>
        </Stack>
    );
});

export default function MessagesPane({
    collab,
    associated_type,
    associated_uuid,
    bubbleSize
}: MessagesPaneProps) {

    const { sendAssociated } = useMessages();
    const { currentIdentity } = React.useContext(UserIdentityContext);

    const [showEditor, setShowEditor] = React.useState(true);
    const { associate } = useFileAssociation();
    const cacheKey = 'comment-' + (associated_uuid || collab?.uuid);
    const [clearId, setClearId] = useState(cacheKey);

    const ShowIcon = getIcon('Add');

    function associateFiles(comment: CommentSubmitProps, m: MessageProps) {
        if (comment.files.length) {
            comment.files.forEach((file) => {
                associate(currentIdentity.uuid, file.uuid, 'message', m.uuid).then((assoc) => {
                    console.log('associated', assoc)
                    Success('File uploaded and associated with message');
                });
                associate(currentIdentity.uuid, file.uuid, associated_type, associated_uuid).then(() => {
                    Success('File uploaded and associated with message');
                });
            });
        }
    }

    function onMessageSubmit(comment: CommentSubmitProps) {

        if (currentIdentity.uuid) {
            sendAssociated(
                currentIdentity.uuid,
                associated_type || '',
                associated_uuid || '',
                comment.content
            )
                .then((m: MessageProps|null) => {
                    if(m) {
                    associateFiles(comment, m);
                    deleteCachedComment(cacheKey);
                        setClearId(Math.random().toString());
                    }
                    else {
                        Error("There was an error sending this message");
                    }
                });

            return true;
        }
        return false;
    }

    function onHide() {
        setShowEditor(false);
    }


    return (
        <Stack
            direction="column"
            sx={{
                minHeight: { xs: undefined, sm: "100dvh" },
            }}

            spacing={0}>

            <MessagesList
                            collab={collab}
                            bubbleSize={bubbleSize}
                associated_type={associated_type}
                associated_uuid={associated_uuid}
            />


            <Box flexGrow={1} />
            <Stack
                sx={{
                    width: '100%',
                    position: { xs: undefined, sm: 'sticky' },
                    bottom: 0,
                }}
            >

                {showEditor &&
                    <CombinedInput
                        clearId={clearId}
                        associated_type={associated_type}
                        associated_uuid={associated_uuid}
                        uuid={associated_uuid}
                        onHide={onHide}
                        onMessageSubmit={onMessageSubmit} />}

                {!showEditor &&
                    <Stack
                        direction="row"
                        width="100%"

                        justifyContent="flex-end" >
                        <Button
                            endDecorator={<ShowIcon />}
                            variant="plain"
                            sx={(theme) => ({
                                mb: 1,
                                mr: 1,
                                backgroundColor: theme.vars.palette.background.surface
                            })}
                            color="neutral"
                            size="sm"
                            onClick={() => setShowEditor(true)}>Send a message</Button>

                    </Stack>
                }

            </Stack>

        </Stack>
    );
}
